<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
        :title="$t('Global.Companies.officesManagement')"
        :companyName="getEditCompanyInfoName"
        icon="mdi-office-building" />
      <v-container fluid class="fill-width">
        <v-sheet class="neu-glow with-radius">
          <v-data-table
            :loading="getLoader"
            :headers="filteredHeaders"
            :items="offices"
            sort-by="officeId"
            class="elevation-1 crud-table px-6 neu-glow"
          >
            <template v-slot:top>
              <v-toolbar flat color="transparent" class="pt-2">
                <v-spacer />
                <v-btn color="primary" v-if="notShowByRol()" dark class="mb-2" @click="newOffice">{{ $t('Global.Offices.newOffice') }}</v-btn>
                <office-dialog
                  v-if="dialogActive"
                  :activate="dialogActive"
                  :item="editedItem"
                  @create-item="createItem"
                  @update-item="updateItem"
                  @close-dialog="dialogActive=false" />
              </v-toolbar>
            </template>
            <template #[`item.actions`] ="{ item }">
              <v-tooltip content-class='custom-tooltip' left>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="editItem(item)"
                  >mdi-pencil</v-icon>
                </template>
                <span>{{ $t('Global.Offices.edit') }}</span>
              </v-tooltip>
              <v-tooltip content-class='custom-tooltip' right>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    size="20"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="deleteItem(item)"
                  >mdi-delete</v-icon>
                </template>
                <span>{{ $t('Global.Offices.delete') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-container>
      <confirm-dialog
        ref="confirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMessage"
      />
    <v-snackbar
        v-model="snackbar"
        absolute
        top
        right
        :timeout="snackbarTimeout"
        :color="snackbarColor"
        class="mt-6"
    >
      <span>{{ snackbarMessage }}</span>
      <v-icon class="ml-2" dark>{{ snackbarIcon }}</v-icon>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          {{ $t("Global.ConfirmDialog.buttonTextClose") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import snackBar from "@/mixins/snackBarMixin";
import loader from "@/mixins/Loader";
import OfficeDialog from './OfficeDialog.vue'
import ConfirmDialog from "@/components/UI/Dialogs/ConfirmDialog";
import {mapGetters} from 'vuex'
import ROL from "@/config/rol-values";
import Utils from "@/mixins/Utils";
import Logout from "@/mixins/Logout";
import OfficeApi from "@/mixins/Services/OfficeApi";


export default {
  name: "Offices",
  components: {
    OfficeDialog,
    ConfirmDialog
  },
  mixins: [OfficeApi, snackBar, loader, Utils, Logout],
  data() {
    return {
      offices: [],
      expanded: [],
      headers: [
        { text: this.$t('Global.Offices.officeId'), value: "callcenterOfficeId", align: "left", sortable: true},
        { text: this.$t('Global.Offices.name'), value: "callcenterOfficeName", align: "left", sortable: false},
        { text: this.$t('Global.Offices.actions'), value: "actions", align: "right", sortable: false}
      ],
      editedItem: null,
      dialogActive: false,
      confirmDialogTitle: "",
      confirmDialogMessage: null,
      rol: ROL
    }
  },
  computed: {
    ...mapGetters(['getEditCompanyInfoName', 'getRoleId']),
    filteredHeaders() {
      if (this.headers.length) {
        return this.notShowByRol()
          ? this.headers : this.headers.filter(h => h.value !== "actions")
      }
      else return []
    }
  },
  methods: {
    async getOfficeList() {
      try{
      this.setLoader(true)
      this.offices = await this.getOffices();
      this.setLoader(false)
      } catch (e) {
        this.offices = []
        console.error('[USERS][getOfficeList] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    newOffice() {
      this.dialogActive = true
      this.editedItem = null
    },
    editItem(item) {
      this.dialogActive = true
      this.editedItem = item
    },
    async createItem(item) {
      this.confirmDialogTitle = this.$t("Global.Offices.newOffice")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.createOffice(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Offices.officeCreatedOk"), "success", "4000", "mdi-check-circle")
        await this.getOfficeList()
      } catch (e) {
        console.error('[OFFICES][createOffice] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async updateItem(item) {
      this.confirmDialogTitle = this.$t("Global.Offices.editOffice")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.updateOffice(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Offices.officeEditedOk"), "success", "4000", "mdi-check-circle")
        await this.getOfficeList()
      } catch (e) {
        console.error('[OFFICES][updateOffice] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async deleteItem(item) {
      this.confirmDialogTitle = this.$t("Global.Offices.deleteOffice")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.deleteOffice(item.officeId)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Offices.officeDeletedOk"), "success", "4000", "mdi-check-circle")
        await this.getOfficeList()
      } catch (e) {
        console.error('[OFFICES][deleteOffice] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    notShowByRol(){
      return ![this.rol.SITE_ADMIN,this.rol.SITE_USER, this.rol.DISPLAY_USER].includes(this.getRoleId)
    }

  },
  mounted() {
    this.getOfficeList()
  }

}
</script>

<style>
  .hide-actions {
    display: none !important;
  }
</style>

